/**
 * Helpers para tradução e manipulação de severidades
 * Centraliza a lógica de severidade usada em todo o sistema
 */

export type SeverityLevel = 0 | 1 | 2 | 3;

/**
 * Mapeamento de cores do Fluent UI para cada nível de severidade
 */
export const SEVERITY_COLORS = {
  0: 'success',  // Baixa - Verde
  1: 'brand',    // Média - Azul (brand)
  2: 'warning',  // Alta - Laranja/Amarelo
  3: 'danger',   // Crítica - Vermelho
} as const;

/**
 * Mapeamento de textos para cada nível de severidade
 */
export const SEVERITY_LABELS = {
  0: 'Baixa',
  1: 'Média',
  2: 'Alta',
  3: 'Crítica',
} as const;

/**
 * Retorna a cor do Fluent UI correspondente ao nível de severidade para componentes Badge
 */
export const getSeverityBadgeColor = (severity: SeverityLevel | number | string): "danger" | "warning" | "brand" | "success" => {

  // Converte para number se necessário
  let numericSeverity: number;
  if (typeof severity === 'string') {
    numericSeverity = parseInt(severity, 10);
  } else if (typeof severity === 'number') {
    numericSeverity = severity;
  } else {
    numericSeverity = 0;
  }

  // Garante que está dentro do range válido
  const clampedSeverity = Math.max(0, Math.min(3, numericSeverity)) as SeverityLevel;

  const colorMap = {
    0: 'success' as const,  // Baixa - verde
    1: 'brand' as const,    // Média - azul
    2: 'warning' as const,  // Alta - laranja/amarelo
    3: 'danger' as const,   // Crítica - vermelho
  };

  const result = colorMap[clampedSeverity];

  return result;
};

/**
 * Retorna a cor CSS para gradientes baseada no nível de severidade
 * Usada especificamente para barras de progresso e gradientes visuais
 */
export const getSeverityGradientColor = (severity: SeverityLevel): string => {
  const colorMap = {
    0: '#107c10', // Baixa - verde
    1: '#0078d4', // Média - azul
    2: '#ff8c00', // Alta - laranja escuro
    3: '#d13438', // Crítica - vermelho
  };
  return colorMap[severity] || colorMap[0];
};

/**
 * Retorna o texto correspondente ao nível de severidade
 */
export const getSeverityText = (severity: SeverityLevel | number | string): string => {

  // Converte para number se necessário
  let numericSeverity: number;
  if (typeof severity === 'string') {
    numericSeverity = parseInt(severity, 10);
  } else if (typeof severity === 'number') {
    numericSeverity = severity;
  } else {
    numericSeverity = 0;
  }

  // Garante que está dentro do range válido
  const clampedSeverity = Math.max(0, Math.min(3, numericSeverity)) as SeverityLevel;

  const result = SEVERITY_LABELS[clampedSeverity];

  return result;
};

/**
 * Retorna o label correspondente ao nível de severidade (alias para getSeverityText)
 */
export const getSeverityLabel = (severity: SeverityLevel): string => {
  const result = getSeverityText(severity);
  return result;
};

/**
 * Converte um nível de severidade para um valor numérico de 0-100 para cálculos
 * Útil para cálculos de média de severidade
 */
export const severityToNumeric = (severity: SeverityLevel): number => {
  return severity;
};

/**
 * Converte um valor numérico para o nível de severidade mais próximo
 */
export const numericToSeverity = (value: number): SeverityLevel => {
  const clamped = Math.max(0, Math.min(3, Math.round(value)));
  return clamped as SeverityLevel;
};

/**
 * Calcula a severidade média de uma lista de severidades
 */
export const calculateAverageSeverity = (severities: SeverityLevel[]): SeverityLevel => {
  if (severities.length === 0) return 0;

  const sum = severities.reduce((acc: number, severity) => acc + severity, 0);
  const average = sum / severities.length;

  return numericToSeverity(average);
};

/**
 * Conta ocorrências de cada nível de severidade
 */
export const countSeverities = (severities: SeverityLevel[]) => {
  const counts = { 0: 0, 1: 0, 2: 0, 3: 0 };

  severities.forEach(severity => {
    counts[severity]++;
  });

  return counts;
};

/**
 * Verifica se uma severidade é considerada crítica (3)
 */
export const isCritical = (severity: SeverityLevel): boolean => {
  return severity === 3;
};

/**
 * Verifica se uma severidade é considerada alta (2 ou 3)
 */
export const isHighSeverity = (severity: SeverityLevel): boolean => {
  return severity >= 2;
};

/**
 * Ordena severidades por prioridade (crítica primeiro, ou seja, maior número primeiro)
 */
export const sortBySeverity = (severities: SeverityLevel[]): SeverityLevel[] => {
  return [...severities].sort((a, b) => b - a); // 3 primeiro (mais crítico)
};

/**
 * Retorna todas as opções de severidade para uso em dropdowns
 */
export const getSeverityOptions = () => {
  return [
    { value: 0, label: SEVERITY_LABELS[0] },
    { value: 1, label: SEVERITY_LABELS[1] },
    { value: 2, label: SEVERITY_LABELS[2] },
    { value: 3, label: SEVERITY_LABELS[3] },
  ];
};